// Precondition: None
// Postcondition: Custom element for displaying tool execution results
// Invariants: Collapsible card showing tool name and result

import { icon } from "../../../shared/icons.js";

if (!customElements.get("ft-chat-tool-result")) {
  interface ToolResult {
    name: string;
    result?: unknown;
    error?: string;
    status?: "pending" | "success" | "error";
  }

  class FtChatToolResult extends HTMLElement {
    private _data: ToolResult | null = null;
    private _expanded = false;

    constructor() {
      super();
      this.attachShadow({ mode: "open" });
    }

    static get observedAttributes(): string[] {
      return ["expanded"];
    }

    get data(): ToolResult | null {
      return this._data;
    }

    set data(value: ToolResult | null) {
      this._data = value;
      this.updateContent();
    }

    get expanded(): boolean {
      return this._expanded;
    }

    set expanded(value: boolean) {
      this._expanded = value;
      if (value) {
        this.setAttribute("expanded", "");
      } else {
        this.removeAttribute("expanded");
      }
      this.updateExpandState();
    }

    connectedCallback(): void {
      this.render();
      this.setupEventListeners();
      // Update content if data was set before connection
      if (this._data) {
        this.updateContent();
      }
    }

    attributeChangedCallback(
      name: string,
      oldValue: string | null,
      newValue: string | null,
    ): void {
      if (name === "expanded") {
        this._expanded = newValue !== null;
        this.updateExpandState();
      }
    }

    private render(): void {
      const style = document.createElement("style");
      style.textContent = this.getStyles();

      const template = document.createElement("template");
      template.innerHTML = this.getTemplate();

      if (this.shadowRoot) {
        this.shadowRoot.appendChild(style);
        this.shadowRoot.appendChild(template.content.cloneNode(true));
      }
    }

    private getStyles(): string {
      return `
        :host {
          display: block;
          margin-top: 8px;
        }

        .tool-card {
          background: var(--input-bg, #ffffff);
          border: 1px solid var(--card-border, #e5e7eb);
          border-radius: 8px;
          overflow: hidden;
        }

        .tool-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          cursor: pointer;
          user-select: none;
        }

        .tool-header:hover {
          background: var(--surface, #f9fafb);
        }

        .tool-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          background: var(--surface, #f3f4f6);
          border-radius: 4px;
          color: var(--muted, #6b7280);
        }

        .tool-icon.success {
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        }

        .tool-icon.pending {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .tool-icon.error {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .tool-name {
          flex: 1;
          font-size: 13px;
          font-weight: 500;
          color: var(--text, #1f2937);
        }

        .expand-icon {
          color: var(--muted, #9ca3af);
          transition: transform 0.2s ease;
        }

        :host([expanded]) .expand-icon {
          transform: rotate(180deg);
        }

        .tool-content {
          display: none;
          padding: 12px;
          border-top: 1px solid var(--card-border, #e5e7eb);
          background: var(--surface, #f9fafb);
        }

        :host([expanded]) .tool-content {
          display: block;
        }

        pre {
          margin: 0;
          font-size: 12px;
          font-family: ui-monospace, monospace;
          white-space: pre-wrap;
          word-break: break-word;
          color: var(--text, #1f2937);
          max-height: 300px;
          overflow-y: auto;
        }

        @media (prefers-color-scheme: dark) {
          .tool-card {
            background: var(--surface, #1f2937);
            border-color: var(--card-border, #374151);
          }

          .tool-header:hover {
            background: var(--panel, #374151);
          }

          .tool-icon {
            background: var(--panel, #374151);
            color: var(--muted, #a3a3a3);
          }

          .tool-name {
            color: var(--text, #f3f4f6);
          }

          .expand-icon {
            color: var(--muted, #a3a3a3);
          }

          .tool-content {
            background: var(--panel, #111827);
            border-color: var(--card-border, #374151);
          }

          pre {
            color: var(--text, #f3f4f6);
          }
        }
      `;
    }

    private getTemplate(): string {
      return `
        <div class="tool-card">
          <div class="tool-header">
            <span class="tool-icon">${icon("settings", 14)}</span>
            <span class="tool-name">Tool</span>
            <span class="expand-icon">${icon("chevron-down", 16)}</span>
          </div>
          <div class="tool-content">
            <pre></pre>
          </div>
        </div>
      `;
    }

    private setupEventListeners(): void {
      const header = this.shadowRoot?.querySelector(".tool-header");
      header?.addEventListener("click", () => {
        this.expanded = !this.expanded;
      });
    }

    private updateContent(): void {
      if (!this._data) return;

      const nameEl = this.shadowRoot?.querySelector(".tool-name");
      const iconEl = this.shadowRoot?.querySelector(".tool-icon");
      const preEl = this.shadowRoot?.querySelector("pre");

      if (nameEl) {
        nameEl.textContent = this.formatToolName(this._data.name);
      }

      if (iconEl) {
        iconEl.classList.remove("success", "pending", "error");
        if (this._data.status) {
          iconEl.classList.add(this._data.status);
        }

        // Update icon based on status
        if (this._data.status === "success") {
          iconEl.innerHTML = icon("check", 14);
        } else if (this._data.status === "pending") {
          iconEl.innerHTML = icon("loader", 14);
        } else if (this._data.status === "error") {
          iconEl.innerHTML = icon("x", 14);
        } else {
          iconEl.innerHTML = icon("settings", 14);
        }
      }

      if (preEl) {
        if (this._data.error) {
          preEl.textContent = this._data.error;
        } else if (this._data.result) {
          preEl.textContent = JSON.stringify(this._data.result, null, 2);
        }
      }
    }

    private updateExpandState(): void {
      // CSS handles the visual state via :host([expanded])
    }

    private formatToolName(name: string): string {
      return name
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    }
  }

  customElements.define("ft-chat-tool-result", FtChatToolResult);
}
